import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  forwardRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormsModule } from '@angular/forms';
import { TaxCode } from '../../models/tax-code.model';
import { BadgeModule } from 'primeng/badge';

export type TaxCodeValueOutput = 'id' | 'percentage';

export interface TaxCodeOption {
  label: string;
  value: number | null;
  percentage?: number;
  is_default?: boolean;
}

@Component({
  selector: 'app-tax-code-select',
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    BadgeModule,
    FloatLabelModule,
  ],
  templateUrl: './tax-code-select.component.html',
  styleUrl: './tax-code-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TaxCodeSelectComponent),
      multi: true,
    },
  ],
})
export class TaxCodeSelectComponent implements ControlValueAccessor, OnInit {
  // Why is this needed?
  private static nextSelectId = 0;

  // And also why is this needed?
  readonly selectInputId = `tax-code-select-${TaxCodeSelectComponent.nextSelectId++}`;

  httpClient = inject(HttpClient);

  /** When true and the control value is null, use the default tax code if one exists (once per control lifecycle). */
  @Input() isNewItem = false;

  /** What to emit as the form value: 'id' (default) or 'percentage'. */
  @Input() valueOutput: TaxCodeValueOutput = 'id';

  /** When true, show the outlined float label ("Tax"). When false, render only the select. */
  @Input() displayLabel = false;

  /** When true, force the select to take full width even when outputting percentage values. */
  @Input() fullWidth = false;

  taxCodes = signal<TaxCode[]>([]);
  taxCodeOptions = computed<TaxCodeOption[]>(() => [
    { label: 'None', value: null, is_default: false },
    ...this.taxCodes().map((tc) => ({
      label: `${tc.name} — ${tc.percentage}%`,
      value: tc.id,
      percentage: tc.percentage,
      is_default: tc.is_default,
    })),
  ]);
  value: number | null = null;
  private _disabledByForm = false;
  private _pendingPercentage: number | null = null;

  get isDisabled(): boolean {
    return this._disabledByForm;
  }

  private onChange = (_: number | null) => {
    void _;
  };
  private onTouched = () => {};

  ngOnInit(): void {
    this.httpClient
      .get<TaxCode[]>('/quotes-api/tax-codes/')
      .subscribe((list) => {
        this.taxCodes.set(list);
        if (this.isNewItem) {
          const default_tax_code = list.find((tc) => tc.is_default);
          if (default_tax_code) {
            this.value = default_tax_code.id;
            this.onChange(
              this.valueOutput === 'percentage'
                ? Number(default_tax_code.percentage)
                : default_tax_code.id,
            );
          }
        } else if (
          this.valueOutput === 'percentage' &&
          this._pendingPercentage != null
        ) {
          this.value = this.findIdByPercentage(this._pendingPercentage);
        }
      });
  }

  onTaxCodeChange(id: number | null): void {
    this.value = id;
    const out =
      this.valueOutput === 'percentage' && id !== null
        ? (this.taxCodeOptions().find((opt) => opt.value === id)?.percentage ??
          null)
        : id;
    this.onChange(out);
    this.onTouched();
  }

  writeValue(val: number | string | null): void {
    if (this.valueOutput === 'percentage') {
      const pct =
        val != null && Number.isFinite(Number(val)) ? Number(val) : null;
      this._pendingPercentage = pct;
      this.value = pct != null ? this.findIdByPercentage(pct) : null;
    } else {
      this._pendingPercentage = null;
      this.value = val as number | null;
    }
  }

  private findIdByPercentage(pct: number): number | null {
    const tc = this.taxCodes().find((c) => Number(c.percentage) === pct);
    return tc?.id ?? null;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByForm = isDisabled;
  }
}
